import React, { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageSquare, X, Send, Shield, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 3000;

/**
 * Floating live chat widget — shows in the bottom-right corner of every
 * page that uses AppLayout. HTTP-polled at 3 s intervals (simpler than
 * websockets for the current scale; trivially upgradable later).
 *
 * Suspended users can read but not post (server enforces too).
 * Admins can delete any message via the trash icon.
 */
export default function ChatWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [onlineCount, setOnlineCount] = useState(0);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [unread, setUnread] = useState(0);
  const lastSeenIdRef = useRef(null);
  const scrollRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!user) return;
    try {
      const r = await axios.get(`${BACKEND_URL}/api/chat/messages`, { withCredentials: true });
      const list = r.data.messages || [];
      setMessages(list);
      setOnlineCount(r.data.online_count || 0);
      // Track unread when widget is closed
      const newest = list[list.length - 1]?.message_id;
      if (!open && lastSeenIdRef.current && newest && newest !== lastSeenIdRef.current) {
        const idx = list.findIndex((m) => m.message_id === lastSeenIdRef.current);
        if (idx >= 0) {
          const newCount = list.length - 1 - idx;
          if (newCount > 0) setUnread((u) => u + newCount);
        }
        lastSeenIdRef.current = newest;
      } else if (open && newest) {
        lastSeenIdRef.current = newest;
        setUnread(0);
      } else if (!lastSeenIdRef.current && newest) {
        lastSeenIdRef.current = newest;
      }
    } catch (_e) { /* silent — chat is non-critical */ }
  }, [user, open]);

  // Poll while logged in
  useEffect(() => {
    if (!user) return undefined;
    refresh();
    const t = setInterval(refresh, POLL_MS);
    return () => clearInterval(t);
  }, [user, refresh]);

  // Auto-scroll to bottom on open + on new message while open
  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [open, messages.length]);

  const send = async () => {
    const text = draft.trim();
    if (!text) return;
    setBusy(true);
    try {
      await axios.post(`${BACKEND_URL}/api/chat/messages`, { body: text }, { withCredentials: true });
      setDraft("");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const deleteMsg = async (messageId) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/chat/messages/${messageId}`, { withCredentials: true });
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  if (!user) return null;

  return (
    <div className="fixed bottom-3 right-3 z-40" data-testid="chat-widget">
      {open ? (
        <div className="w-[340px] sm:w-[380px] h-[480px] bg-black/95 border border-white/15 backdrop-blur-xl flex flex-col">
          <div className="flex items-center gap-3 px-3 py-2 border-b border-white/10">
            <MessageSquare className="w-4 h-4 text-[#FFCC00]" />
            <div className="flex-1 min-w-0">
              <div className="font-heading text-sm uppercase tracking-tight">Live Chat</div>
              <div className="text-[10px] text-[#34C759] tracking-[0.2em] uppercase" data-testid="chat-online-count">
                {onlineCount} online
              </div>
            </div>
            <button
              onClick={() => { setOpen(false); setUnread(0); }}
              data-testid="chat-close"
              className="p-1.5 hover:bg-white/5"
              aria-label="Close chat"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-2 space-y-2" data-testid="chat-messages">
            {messages.length === 0 ? (
              <div className="text-center text-white/40 text-xs pt-12">
                Be the first to say hi!
              </div>
            ) : (
              messages.map((m) => {
                const mine = m.user_id === user.user_id;
                return (
                  <div
                    key={m.message_id}
                    className={`flex gap-2 ${mine ? "flex-row-reverse" : ""}`}
                    data-testid={`chat-message-${m.message_id}`}
                  >
                    {m.picture ? (
                      <img src={m.picture} alt={m.user_name}
                           className="w-7 h-7 rounded-full object-cover flex-shrink-0 border border-white/10" />
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-[#FF3B30] flex items-center justify-center text-black text-xs font-bold flex-shrink-0">
                        {m.user_name?.[0]?.toUpperCase() ?? "?"}
                      </div>
                    )}
                    <div className={`flex-1 min-w-0 ${mine ? "text-right" : ""}`}>
                      <div className={`flex items-center gap-1.5 text-[10px] text-white/40 ${mine ? "justify-end" : ""}`}>
                        {m.is_admin && <Shield className="w-2.5 h-2.5 text-[#FFCC00]" />}
                        <span className="font-medium">{m.user_name}</span>
                        {user.is_admin && !mine && (
                          <button
                            onClick={() => deleteMsg(m.message_id)}
                            data-testid={`chat-delete-${m.message_id}`}
                            className="ml-1 text-white/30 hover:text-[#FF3B30]"
                            title="Delete (admin)"
                          >
                            <Trash2 className="w-2.5 h-2.5" />
                          </button>
                        )}
                      </div>
                      <div className={`inline-block mt-0.5 px-2.5 py-1.5 text-sm break-words ${mine ? "bg-[#FFCC00]/15 text-white border border-[#FFCC00]/30" : "bg-white/5 text-white border border-white/10"}`}>
                        {m.body}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {user.is_suspended ? (
            <div className="px-3 py-2 border-t border-white/10 text-[11px] text-[#FF3B30] text-center">
              Suspended — chat is read-only.
            </div>
          ) : (
            <div className="flex items-stretch border-t border-white/10">
              <input
                type="text"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Say something…"
                maxLength={280}
                disabled={busy}
                data-testid="chat-input"
                className="flex-1 bg-transparent px-3 text-sm focus:outline-none placeholder:text-white/30"
              />
              <button
                onClick={send}
                disabled={busy || !draft.trim()}
                data-testid="chat-send"
                className="px-3 bg-[#FFCC00] text-black hover:bg-[#FFD93D] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                aria-label="Send"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      ) : (
        <button
          onClick={() => { setOpen(true); setUnread(0); }}
          data-testid="chat-toggle"
          className="relative w-12 h-12 rounded-full bg-[#FFCC00] text-black hover:bg-[#FFD93D] shadow-2xl flex items-center justify-center transition-colors"
          aria-label="Open chat"
        >
          <MessageSquare className="w-5 h-5" />
          {unread > 0 && (
            <span
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-[#FF3B30] border-2 border-black px-1 text-[10px] font-bold text-white flex items-center justify-center"
              data-testid="chat-unread-badge"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      )}
    </div>
  );
}
